import { ROUTE_SIGN_IN } from '@/constants/routes';
import { getRoute } from './get-route';

/**
 * Reasons for landing on /sign-in with an interstitial banner instead of the
 * default AuthKit redirect.
 *
 * - `no_account`: WorkOS auth succeeded for an email with no internal Catch
 *   account. The banner offers "Sign up" / "Try a different email" CTAs and
 *   blocks the SignIn page's default `signIn()` useEffect so the user doesn't
 *   loop through AuthKit (incident 2026-05-13).
 * - `already_member`: VIP-comp short-circuit from the payment page — the
 *   visitor's email matches an existing member, so we offer "Continue to sign
 *   in" instead of letting them pay twice (incident 2026-05-12).
 */
export const BOUNCE_REASONS = ['no_account', 'already_member'] as const;
export type BounceReason = (typeof BOUNCE_REASONS)[number];

export function isBounceReason(value: string | null | undefined): value is BounceReason {
  return value !== null && value !== undefined && (BOUNCE_REASONS as readonly string[]).includes(value);
}

/**
 * Build a /sign-in URL with the interstitial reason and (optionally) the
 * email the user authenticated with — used by the banner copy ("We couldn't
 * find a Catch account for <email>").
 *
 * Centralized so the route loader, the customFetcher 401 handler, and
 * AuthGuard all build the same shape; if we change the param names we
 * change them in one place.
 */
export function signInBounceUrl(reason: BounceReason, email?: string | null): string {
  const params = new URLSearchParams({ reason });
  if (email) {
    params.set('email', email);
  }
  return `${getRoute(ROUTE_SIGN_IN)}?${params.toString()}`;
}
